import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import blogData from "../blog/blog.json";
import {
  Box,
  Typography,
  IconButton,
  Container,
  Tooltip,
  Divider,
  Snackbar,
  Link,
  useTheme,
} from "@mui/material";
import SEO from "../components/misc/SEO";
import ShareIcon from "@mui/icons-material/Share";
import ReactMarkdown from "react-markdown";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BlogPostRow from "../components/blog/BlogPostRow";
import ProjectCard from "../components/blog/ProjectCard";

const BlogPost = () => {
  const theme = useTheme();
  const { id } = useParams();
  const post = blogData.find((p) => p.id === id);

  const [markdown, setMarkdown] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (post?.post) {
      fetch(post.post)
        .then((res) => res.text())
        .then((text) => {
          setMarkdown(text);
          window.scrollTo({ top: 0, behavior: "instant" });
        })
        .catch((err) => console.error("Failed to load markdown:", err));
    }
  }, [post]);

  if (!post) {
    return <Typography variant="h6">Post not found.</Typography>;
  }

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <SEO
        title={`${post?.title ? post.title : "Untitled"} | Aaron Hunt`}
        description={post?.summary || "Check out this article on my blog!"}
        image={`https://www.aaronhunt.dev${post?.image || "/blog/i/pg.jpg"}`}
      />

      {/* Full-width image with attribution */}
      {post.image && (
        <>
          <Box sx={{ position: "relative" }}>
            <Box
              component="img"
              src={post.image}
              alt={post.title}
              sx={{
                width: "100%",
                maxHeight: "300px",
                objectFit: "cover",
                display: "block",
                borderBottom: "1px solid",
                borderColor: "divider",
                filter: "blur(2px)",
              }}
            />

            {post.imageFromPexels && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 12,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  px: 1,
                  py: 0.5,
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                }}
              >
                <Link
                  href="https://www.pexels.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  underline="hover"
                >
                  Photo from Pexels
                </Link>
              </Box>
            )}
          </Box>
        </>
      )}
      <Box className="backgroundGrid">
        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            pt: 4,
            backgroundColor: "background.default",
            borderLeft: "1px solid",
            borderRight: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ maxWidth: "900px", mx: "auto", pb: 4 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 2,
                fontFamily: "monospace",
                color: "text.secondary",
                opacity: 0.7,
                userSelect: "none",
              }}
            >
              begin article /{post.id}
            </Typography>

            <Box sx={{ position: "absolute", top: 16, right: 16 }}>
              <Tooltip title="Copy link">
                <IconButton onClick={handleShare} color="primary">
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="h4" gutterBottom>
              {post.title}
            </Typography>

            <Typography
              variant="subtitle2"
              color="text.secondary"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CalendarTodayIcon fontSize="small" />
              {new Date(post.posted).toLocaleDateString()} – {post.category}
            </Typography>

            <Box sx={{ mt: 4 }}>
              {post.isProject && <ProjectCard post={post} />}

              <ReactMarkdown
                components={{
                  p: ({ node, ...props }) => (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 300 }}
                      paragraph
                      {...props}
                    />
                  ),
                  a: ({ node, ...props }) => (
                    <Link
                      {...props}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      underline="hover"
                      sx={{ wordBreak: "break-word" }}
                    />
                  ),
                  blockquote: ({ node, ...props }) => (
                    <Box
                      component="blockquote"
                      sx={{
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                        backgroundColor: theme.palette.action.hover,
                        pl: 4,
                        my: 2,
                        py: 2,
                        "& p": {
                          marginBottom: 0,
                          marginTop: 0,
                          pr: 2,
                        },
                      }}
                      {...props}
                    />
                  ),
                  code: ({ children, ...props }) => (
                    <Box
                      component="code"
                      sx={{
                        backgroundColor: theme.palette.action.selected,
                        color: theme.palette.text.primary,
                        fontSize: "0.85rem",
                        fontFamily: "monospace",
                        px: 0.5,
                        py: 0.2,
                        borderRadius: 1,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                      {...props}
                    >
                      {children}
                    </Box>
                  ),
                }}
              >
                {markdown}
              </ReactMarkdown>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6">Read More</Typography>
            <BlogPostRow currentPostId={post.id} />
          </Box>
        </Container>
      </Box>

      {/* Snackbar for copied link */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1000}
        onClose={handleSnackbarClose}
        message="Copied link to clipboard."
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default BlogPost;
